/**
 * Staffology Design System -- Theme
 * Version: 2.0.0
 *
 * Generated from the v2.0 structural blueprint (theme-v2.0-original.ts)
 * with values sourced from colors_and_type.css.
 *
 * Both formats are kept in sync intentionally -- pick the one that
 * fits each consumption pattern:
 *
 *   - CSS variables (var(--brand)): for global styles, third-party
 *     CSS, and components that don't need theme switching.
 *   - theme.ts (${({ theme }) => theme.colors.brand}): for typed
 *     access inside styled-components.
 *
 * Light mode only. Dark mode is deferred to v2.1.
 */

export const theme = {
  colors: {
    /* Brand -- single-instance interactive moments */
    brand: '#03A9FC',
    brandHover: '#0490D9',
    brandActive: '#0277B6',
    brand100: '#E0F5FE',
    brand50: '#F0FAFF',

    /* Primary navy -- structure and mass-instance controls */
    primary: {
      900: '#0A1533',
      800: '#0C2647',
      700: '#1E3655',
      600: '#39495D',
      500: '#445469',
      200: '#C7CFD9',
      100: '#E8ECF2',
    },

    /* Neutrals */
    neutral: {
      900: '#212529',
      800: '#343A40',
      700: '#495057',
      600: '#6C757D',
      500: '#ADB5BD',
      400: '#CED4DA',
      300: '#DEE2E6',
      200: '#E9ECEF',
      100: '#F8F9FA',
      50: '#FAFBFC',
    },
    white: '#FFFFFF',
    black: '#000000',

    /* Secondary blue -- data viz only, never UI controls */
    blue: {
      500: '#4976F4',
      100: '#EDF1FE',
      800: '#1E336E',
    },

    /* Status semantic tokens */
    status: {
      success: {500: '#639922', 100: '#EAF3DE', 800: '#27500A'},
      warning: {500: '#BA7517', 100: '#FAEEDA', 800: '#633806'},
      error: {500: '#A32D2D', 100: '#FCEBEB', 800: '#791F1F'},
      info: {500: '#185FA5', 100: '#E6F1FB', 800: '#042C53'},
      pending: {500: '#4976F4', 100: '#EDF1FE', 800: '#1E336E'},
    },

    /* Avatar fallback palette -- deterministic by hash of name */
    avatar: {
      blue: {bg: '#E6F1FB', fg: '#185FA5'},
      amber: {bg: '#FAEEDA', fg: '#854F0B'},
      purple: {bg: '#EEEDFE', fg: '#3C3489'},
      coral: {bg: '#FAECE7', fg: '#712B13'},
      teal: {bg: '#E1F5EE', fg: '#085041'},
      pink: {bg: '#FBEAF0', fg: '#72243E'},
    },
  },

  typography: {
    fontFamily: {
      sans: "'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
      mono: "'JetBrains Mono', ui-monospace, 'SF Mono', Monaco, monospace",
    },
    fontSize: {
      h1: '1.75rem', // 28px
      h2: '1.375rem', // 22px
      h3: '1.125rem', // 18px
      subtitle: '1rem', // 16px
      body: '0.875rem', // 14px -- minimum body
      caption: '0.75rem', // 12px
      micro: '0.6875rem', // 11px -- column headers only
    },
    fontWeight: {
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.2,
      normal: 1.5,
      loose: 1.7,
    },
    letterSpacing: {
      tight: '-0.05em',
      normal: '-0.0125em',
      wide: '0.05em',
    },
  },

  spacing: {
    1: '0.25rem', // 4px
    2: '0.5rem', // 8px
    3: '0.75rem', // 12px
    4: '1rem', // 16px
    5: '1.25rem', // 20px
    6: '1.5rem', // 24px
    8: '2rem', // 32px
    10: '2.5rem', // 40px
    12: '3rem', // 48px
    14: '3.5rem', // 56px
    16: '4rem', // 64px
    20: '5rem', // 80px
    24: '6rem', // 96px
    32: '8rem', // 128px

    /* Semantic -- use these when context is clear */
    cardPadding: '1rem', // var(--space-4)
    cardPaddingLg: '1.5rem', // var(--space-6)
    section: '2rem', // var(--space-8)
    sectionLg: '3rem', // var(--space-12)
    formGap: '1rem', // var(--space-4)
    pagePadding: '1.5rem', // var(--space-6)
  },

  radius: {
    sm: '8px',
    md: '12px',
    lg: '16px',
    full: '999px',
  },

  borders: {
    thin: '1px',
    thick: '2px',
  },

  shadows: {
    strong: '0 0 1px rgba(33, 37, 41, 0.32), 0 4px 6px rgba(33, 37, 41, 0.20)',
    midStrong: '0 0 1px rgba(33, 37, 41, 0.08), 0 2px 2px rgba(33, 37, 41, 0.06)',
    regular: '0 1px 8px rgba(0, 0, 0, 0.03)',
    elevated: '0 4px 6px -2px rgba(10, 13, 18, 0.05), 0 12px 16px -4px rgba(10, 13, 18, 0.10)',
    modal: '0 4px 24px rgba(0, 0, 0, 0.10)',
  },

  density: {
    data: {
      rowHeight: '2.5rem', // 40px
      rowPadding: '0.5rem 0.75rem',
    },
    comfortable: {
      rowHeight: '3.5rem', // 56px
      rowPadding: '0.875rem 1.25rem',
    },
  },

  motion: {
    fast: '150ms',
    base: '250ms',
    slow: '400ms',
    ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },

  zIndex: {
    base: 0,
    elevated: 10,
    sticky: 100,
    dropdown: 1000,
    modal: 2000,
    toast: 3000,
    tooltip: 4000,
  },

  containers: {
    sm: '40rem', // 640px
    md: '48rem', // 768px
    lg: '64rem', // 1024px
    xl: '80rem', // 1280px
  },

  /* Component-level tokens -- derived from foundations */
  components: {
    focus: {
      ring: '0 0 0 3px #E0F5FE',
      border: '#0277B6',
    },
    button: {
      heightSm: '2rem', // 32px
      heightMd: '2.5rem', // 40px
      heightLg: '3rem', // 48px
    },
    input: {
      heightSm: '2.375rem', // 38px
      heightMd: '2.75rem', // 44px
      heightLg: '2.875rem', // 46px
    },
    touch: {
      min: '44px',
    },
  },
} as const

export type Theme = typeof theme

/**
 * Deterministic avatar color picker.
 * Same name -> same color. Never random.
 * Used by Avatar component for fallback when no photo is uploaded.
 *
 * The logged-in user's avatar bypasses this and uses primary[700] (navy).
 */
export const avatarColorFromName = (name: string): keyof Theme['colors']['avatar'] => {
  const palette: (keyof Theme['colors']['avatar'])[] = [
    'blue',
    'amber',
    'purple',
    'coral',
    'teal',
    'pink',
  ]
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0
  }
  return palette[hash % palette.length]
}
